import React from 'react';

import { defineMessages, type MessageDescriptor } from 'react-intl';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import { logException } from '@atlaskit/editor-common/monitoring';
import type {
	QuickInsertItem,
	QuickInsertActionInsert,
} from '@atlaskit/editor-common/provider-factory';
import type { PMPluginFactoryParams, QuickInsertHandler } from '@atlaskit/editor-common/types';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Snippet, TemplateType } from '@atlassian/editor-snippets-provider/types';

import { addSnippetLoader, removeSnippetLoaderAndInsert } from './pm-plugins/loaderTransactions';
import { snippetsPluginKey, createPlugin } from './pm-plugins/main';
import { isAdfNodeCompatibleWithSchema } from './pm-plugins/schemaCompatibility';
import {
	copySnippetMediaForInsertion,
	getSnippetMediaAttrs,
	hasSnippetMediaNodes,
} from './pm-plugins/snippetMedia';
import type { SnippetsPlugin } from './snippetsPluginType';
import { getSnippetQuickInsertComponents } from './ui/quick-insert/getSnippetQuickInsertComponents';
import { selectEligibleSnippets } from './utils/selectEligibleSnippets';

const SNIPPET_KEY_PREFIX = 'snippet-';

const messages = defineMessages({
	template: {
		id: 'editor-plugin-snippets.snippetsPlugin.template',
		defaultMessage: 'template',
		description: 'Keyword for template search in quick insert',
	},
	block: {
		id: 'editor-plugin-snippets.snippetsPlugin.block',
		defaultMessage: 'block',
		description: 'Keyword for block search in quick insert',
	},
	snippetBy: {
		id: 'editor-plugin-snippets.snippetsPlugin.snippetBy',
		defaultMessage: 'by {name}',
		description: 'Description text showing who created the snippet, with the creator name',
	},
});

const extractKeywords = (intl?: {
	formatMessage: (
		descriptor: MessageDescriptor,
		values?: Record<string, string | number | boolean | Date>,
	) => string;
}): string[] => {
	if (!intl) {
		logException(new Error('Missing intl for snippet quick insert keywords'), {
			location: 'editor-plugin-snippets/extractKeywords',
		});
		return [];
	}

	return Array.from(
		new Set([
			intl.formatMessage(messages.template).toLowerCase(),
			intl.formatMessage(messages.block).toLowerCase(),
		]),
	);
};

const getSnippetDescription = (
	snippet: Snippet,
	intl?: {
		formatMessage: (
			descriptor: MessageDescriptor,
			values?: Record<string, string | number | boolean | Date>,
		) => string;
	},
): string | undefined => {
	if (snippet.description) {
		return snippet.description;
	}

	if (!intl) {
		logException(new Error('Missing intl for snippet quick insert description'), {
			location: 'editor-plugin-snippets/getSnippetDescription',
		});
		return `by ${snippet.createdBy}`;
	}

	return intl.formatMessage(messages.snippetBy, { name: snippet.createdBy });
};

const trackSnippetMediaPaste = (
	mediaAttrs: ReturnType<typeof getSnippetMediaAttrs>,
	api: Parameters<SnippetsPlugin>[0]['api'],
): void => {
	mediaAttrs.forEach((attrs) => {
		if (!api?.media?.commands?.trackMediaPaste) {
			return;
		}

		api.core.actions.execute(api.media.commands.trackMediaPaste(attrs));
	});
};

interface InsertSnippetParams {
	adf: ADFEntity[];
	api: Parameters<SnippetsPlugin>[0]['api'];
	insert: QuickInsertActionInsert;
	snippetId: string;
	state: EditorState;
	templateType?: TemplateType;
}

function insertSnippetContent({
	insert,
	state,
	api,
	adf,
	snippetId,
	templateType,
}: InsertSnippetParams): ReturnType<QuickInsertItem['action']> {
	const fireAnalyticsEvent = api?.analytics?.actions?.fireAnalyticsEvent;

	const hasMedia = Array.isArray(adf) && hasSnippetMediaNodes(adf);

	try {
		if (!hasMedia) {
			const fragment = Fragment.fromJSON(state.schema, adf);
			const result = insert(fragment);
			fireAnalyticsEvent?.({
				action: ACTION.INSERTED,
				actionSubject: ACTION_SUBJECT.DOCUMENT,
				actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
				attributes: {
					inputMethod: INPUT_METHOD.QUICK_INSERT,
					snippetId,
					templateType,
				},
				eventType: EVENT_TYPE.TRACK,
			});
			return result;
		}
	} catch (error) {
		fireAnalyticsEvent?.({
			action: ACTION.FAILED_TO_INSERT,
			actionSubject: ACTION_SUBJECT.DOCUMENT,
			actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
			attributes: {
				reason: 'parseError',
				snippetId,
				templateType,
			},
			eventType: EVENT_TYPE.OPERATIONAL,
		});
		logException(error as Error, {
			location: 'editor-plugin-snippets/insertSnippetContent',
		});
		return false;
	}

	const dismissTr = insert('');

	// Create a loader for the SnippetLoader component while registering media copy intents.
	const loaderId = crypto.randomUUID();
	const loaderPosAtAdd = addSnippetLoader({ api, dismissTr, loaderId });

	copySnippetMediaForInsertion(adf, {
		mediaProvider: api?.media?.sharedState.currentState()?.mediaProvider,
		editorContextId:
			api?.contextIdentifier?.sharedState.currentState()?.contextIdentifierProvider?.objectId,
	})
		.then(
			({
				status,
				adf: adfForInsertion,
				numTotalMediaFiles,
				numFailedMediaFiles,
				failedMediaAttrs,
			}) => {
				trackSnippetMediaPaste(failedMediaAttrs, api);

				try {
					removeSnippetLoaderAndInsert({ api, loaderId, loaderPosAtAdd, adf: adfForInsertion });
				} catch (error) {
					fireAnalyticsEvent?.({
						action: ACTION.FAILED_TO_INSERT,
						actionSubject: ACTION_SUBJECT.DOCUMENT,
						actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
						attributes: {
							reason: 'parseError',
							snippetId,
							templateType,
						},
						eventType: EVENT_TYPE.OPERATIONAL,
					});
					logException(error as Error, {
						location: 'editor-plugin-snippets/insertSnippetContent',
					});
					return dismissTr;
				}

				fireAnalyticsEvent?.({
					action: ACTION.INSERTED,
					actionSubject: ACTION_SUBJECT.DOCUMENT,
					actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
					attributes: {
						inputMethod: INPUT_METHOD.QUICK_INSERT,
						snippetId,
						templateType,
						hadMedia: true,
					},
					eventType: EVENT_TYPE.TRACK,
				});
				if (status !== 'copied' && numFailedMediaFiles > 0) {
					fireAnalyticsEvent?.({
						action: ACTION.FAILED_TO_INSERT,
						actionSubject: ACTION_SUBJECT.DOCUMENT,
						actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
						attributes: {
							reason: 'snippetMediaDirectCopyFailed',
							snippetMediaCopyStatus: status,
							snippetId,
							templateType,
							numTotalMediaFiles,
							numFailedMediaFiles,
						},
						eventType: EVENT_TYPE.OPERATIONAL,
					});
					logException(new Error('Partial media copy intent failure'), {
						location: 'editor-plugin-snippets/insertSnippetContent',
					});
				}
			},
		)
		.catch((error) => {
			const mediaAttrs = getSnippetMediaAttrs(adf);
			trackSnippetMediaPaste(mediaAttrs, api);

			fireAnalyticsEvent?.({
				action: ACTION.FAILED_TO_INSERT,
				actionSubject: ACTION_SUBJECT.DOCUMENT,
				actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
				attributes: {
					reason: 'snippetMediaDirectCopyUnexpectedError',
					snippetId,
					templateType,
					numTotalMediaFiles: mediaAttrs.length,
					numFailedMediaFiles: mediaAttrs.length,
				},
				eventType: EVENT_TYPE.OPERATIONAL,
			});
			logException(error as Error, {
				location: 'editor-plugin-snippets/insertSnippetContent',
			});

			try {
				removeSnippetLoaderAndInsert({ api, loaderId, loaderPosAtAdd, adf });
			} catch {
				fireAnalyticsEvent?.({
					action: ACTION.FAILED_TO_INSERT,
					actionSubject: ACTION_SUBJECT.DOCUMENT,
					actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
					attributes: {
						reason: 'parseError',
						snippetId,
						templateType,
					},
					eventType: EVENT_TYPE.OPERATIONAL,
				});
				return false;
			}
		});

	return dismissTr;
}

export const mapToQuickInsertItem = (
	snippet: Snippet,
	api?: Parameters<SnippetsPlugin>[0]['api'],
	intl?: {
		formatMessage: (
			descriptor: MessageDescriptor,
			values?: Record<string, string | number | boolean | Date>,
		) => string;
	},
): QuickInsertItem => ({
	key: `${SNIPPET_KEY_PREFIX}${snippet.snippetId}`,
	title: snippet.name,
	description: getSnippetDescription(snippet, intl),
	keywords: extractKeywords(intl),
	isDisabledOffline: true,
	icon: () => <TemplateIcon label="Snippet" />,
	action: (insert: QuickInsertActionInsert, state: EditorState) => {
		const { snippetId, templateType } = snippet;
		const adf = snippet.body.content?.filter((node) => node !== undefined);
		if (!adf || adf.length === 0) {
			api?.analytics?.actions?.fireAnalyticsEvent?.({
				action: ACTION.FAILED_TO_INSERT,
				actionSubject: ACTION_SUBJECT.DOCUMENT,
				actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
				attributes: {
					reason: 'emptyBody',
					snippetId,
					templateType,
				},
				eventType: EVENT_TYPE.OPERATIONAL,
			});
			logException(new Error('Empty snippet body'), {
				location: 'editor-plugin-snippets/mapToQuickInsertItem',
			});
			return false;
		}

		return insertSnippetContent({ insert, state, api, adf, snippetId, templateType });
	},
});

export const snippetsPlugin: SnippetsPlugin = ({ config, api }) => {
	config.snippetsProvider
		.getSnippets()
		.then((snippets: Snippet[]) => {
			api?.core?.actions.execute(({ tr }) => {
				const schema = tr.doc.type.schema;
				const isConfluencePolicyEnabled = isExperimentEnabled(
					'platform_editor_snippets_plugin_confluence',
				);
				const compatibleSnippets = snippets.filter((snippet) => {
					if (!isConfluencePolicyEnabled) {
						return isAdfNodeCompatibleWithSchema(snippet.body, schema);
					}

					try {
						if (!isAdfNodeCompatibleWithSchema(snippet.body, schema)) {
							return false;
						}

						const snippetNode = schema.nodeFromJSON(snippet.body);
						if (snippetNode.childCount > 0) {
							snippetNode.check();
						}
						return true;
					} catch {
						return false;
					}
				});
				const eligibleSnippets = isConfluencePolicyEnabled
					? selectEligibleSnippets(compatibleSnippets)
					: compatibleSnippets;

				if (!isExperimentEnabled('platform_editor_slash_command')) {
					if (!api?.quickInsert) {
						return null;
					}

					const quickInsertItems: QuickInsertHandler = (intl) =>
						eligibleSnippets.map((snippet) => mapToQuickInsertItem(snippet, api, intl));

					return api.quickInsert.commands.addQuickInsertItem(quickInsertItems)({ tr });
				}

				api?.uiControlRegistry?.actions.register(
					getSnippetQuickInsertComponents({
						api,
						getItem: mapToQuickInsertItem,
						snippets: eligibleSnippets,
					}),
				);
				return tr;
			});
		})
		.catch((error) => {
			logException(error as Error, {
				location: 'editor-plugin-snippets/snippetsPlugin/getSnippets',
			});
		});

	return {
		name: 'snippets',

		getSharedState(editorState) {
			if (!editorState) {
				return undefined;
			}
			return snippetsPluginKey.getState(editorState) ?? undefined;
		},

		pmPlugins() {
			return [
				{
					name: 'snippetsLoader',
					plugin: ({ portalProviderAPI }: PMPluginFactoryParams) => createPlugin(portalProviderAPI),
				},
			];
		},
	};
};
